import Alert from "@/components/core/alert";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { Form, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { Textarea } from "@/components/core/forms/textarea";
import { caseCategories } from "@/constants/case-categories";
import { collectionTypes } from "@/constants/collection-types";
import { courtLevels } from "@/constants/court-levels";
import { courtStations } from "@/constants/court-stations";
import { courtTypes } from "@/constants/court-types";
import { partyTypes } from "@/constants/party-types";
import { payorTypes } from "@/constants/payor-types";
import { provinceList } from "@/constants/province-list";
import { regionList } from "@/constants/region-list";
import { serviceTypes } from "@/constants/service-types";
import fees from "@/data/fee-breakdown";
import type { Fee } from "@/data/fee-breakdown";
import { Route } from "@/routes/_backoffice.case-classification_.$caseClassificationId";
import { IconAlertCircle } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import type { CaseRecord } from "../case-record/types/case-records";
import { caseTypesQueryOptions } from "../dashboard/api/queries/case-type-queries";
import CaseAttachments from "./case-attachments";
import CaseCustomAttachments from "./case-custom-attachments";

export default function CaseInfoView({
	data,
	attachment,
	remarks,
	caseNumber,
	payments,
	upload,
}: {
	data: CaseRecord;
	attachment: boolean;
	remarks: boolean;
	caseNumber: boolean;
	payments: boolean;
	upload: boolean;
}) {
	const search = Route.useSearch();
	const form = useForm({
		defaultValues: {
			id: null,
			case_number: data.case_number || "",
			service_type: data.service_type || "",
			court_level_id: data.court_level_id || "",
			court_type_id: data.court_type_id || "",
			region_code: data.region_code || "",
			province_code: data.province_code || "",
			court_station_id: data.court_station_id || "",
			party_type: "Petitioner",
			last_name: data.last_name || "",
			middle_name: data.middle_name || "",
			first_name: data.first_name || "",
			email: data.email || "",
			mobile_number: data.mobile_number || "",
			summons_count: data.summons_count || "",
			status: data.status || "",
			payment_status: data.payment_status || "",
			filed_at: data.filed_at || "",
			created_at: data.created_at || "",
			station: data.station || "",
			case_type: data.case_type_id || "",
			case_title: data.case_title || "",
			case_category: data.case_category || "",
			case_category_id: data.case_category_id || "",
			court_category: data.court_category || "",
			court_level: data.court_level || "",
			with_fee_exemption: "Yes",
			payor_type: "Individual",
			court_type: "Regional Trial Court",
			province: "Manila",
			region: "NCR",
			court_office: "MANILA CITY",
			condition: "Indigent Litigants",
			assessed_value: "10000",
			number_of_summons: "2",
			attachment: "",
		},
	});

	const [serviceType, setServiceType] = useState("File Case, Supplemental Pleadings, and Answers");

	const caseTypeQuery = useQuery(
		caseTypesQueryOptions({
			...search,
			case_category_id: data?.case_category_id,
			status: "active",
		}),
	);
	const caseTypes = caseTypeQuery?.data?.data.map((item) => ({
		label: item?.name,
		value: item?.id,
	}));

	const columns: ColumnDef<Fee>[] = [
		{
			id: "name",
			label: "Particulars",
			cell: ({ data }) => data.name,
		},
		{
			id: "filing_fee",
			label: "Filing Fee",
			cell: ({ data }) => <span className="font-mono text-base">{data.filing_fee}</span>,
		},
		{
			id: "miscellaneous_fee",
			label: "Miscellaneous Fee",
			cell: ({ data }) => <span className="font-mono text-base">{data.miscellaneous_fee}</span>,
		},
		{
			id: "total",
			label: "Sub Total",
			cell: ({ data }) => <span className="font-mono text-base">{data.total}</span>,
		},
	];

	return (
		<Form {...form}>
			<div className="flex flex-col w-full gap-4 mt-4">
				{remarks !== false && (
					<Alert type="info" allowClose={true} title="Remarks" icon={<IconAlertCircle size={20} stroke={2} />}>
						Your remarks goes here
					</Alert>
				)}

				<div className="flex flex-col w-full gap-8">
					<div className="flex flex-col w-full gap-3">
						<div className="flex flex-col w-full">
							<span className="text-base font-semibold text-foreground-dark">Request Details</span>
							<span className="text-base text-foreground/70">Summary of the Request</span>
						</div>
						<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
							<div className="col-span-6 w-full">
								<FormField
									control={form.control}
									name="service_type"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Service Type</FormLabel>
												<FormDescription>Type of service being requested</FormDescription>
											</div>
											<FormGroup>
												<Select
													onValueChange={async (value) => {
														field.onChange(value);
														setServiceType(value);
													}}
													defaultValue={field.value}
													options={serviceTypes}
												/>
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-6 w-full">
								<FormField
									control={form.control}
									name="payor_type"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Payor Type</FormLabel>
												<FormDescription>Responsible party for payment</FormDescription>
											</div>
											<FormGroup>
												<Select onValueChange={field.onChange} defaultValue={field.value} options={payorTypes} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>

					<div className="flex flex-col w-full gap-3">
						<div className="flex flex-col w-full">
							<span className="text-base font-semibold text-foreground-dark">Court Details</span>
							<span className="text-base text-foreground/70">Specific court and its jurisdiction</span>
						</div>
						<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
							<div className="col-span-6 w-full">
								<FormField
									control={form.control}
									name="court_category"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Court Level</FormLabel>
												<FormDescription />
											</div>
											<FormGroup>
												<Select
													onValueChange={async (value) => {
														field.onChange(value);
													}}
													defaultValue={field.value}
													options={courtLevels}
												/>
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-6 w-full">
								<FormField
									control={form.control}
									name="court_level"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Court Type</FormLabel>
												<FormDescription />
											</div>
											<FormGroup>
												<Select onValueChange={field.onChange} defaultValue={field.value} options={courtTypes} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-6 w-full">
								<FormField
									control={form.control}
									name="region"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Region</FormLabel>
												<FormDescription />
											</div>
											<FormGroup>
												<Select onValueChange={field.onChange} defaultValue={field.value} options={regionList} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-6 w-full">
								<FormField
									control={form.control}
									name="province"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Province</FormLabel>
												<FormDescription />
											</div>
											<FormGroup>
												<Select onValueChange={field.onChange} defaultValue={field.value} options={provinceList} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-12 w-full">
								<FormField
									control={form.control}
									name="court_office"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Court Station/Office</FormLabel>
												<FormDescription />
											</div>
											<FormGroup>
												<Select onValueChange={field.onChange} defaultValue={field.value} options={courtStations} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>

					{serviceType !== "File Case, Supplemental Pleadings, and Answers" && (
						<div className="flex flex-col w-full gap-3">
							<div className="flex flex-col w-full">
								<span className="text-base font-semibold text-foreground-dark">Application Details</span>
								<span className="text-base text-foreground/70">Some form group requires description, this is the right place for that.</span>
							</div>
							<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
								<div className="col-span-12 w-full">
									<FormField
										control={form.control}
										name="collection_type"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Collection Type</FormLabel>
													<FormDescription />
												</div>
												<FormGroup>
													<Select onValueChange={field.onChange} defaultValue={field.value} options={collectionTypes} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>
						</div>
					)}

					{serviceType === "File Case, Supplemental Pleadings, and Answers" && (
						<div className="flex flex-col w-full gap-3">
							<div className="flex flex-col w-full">
								<span className="text-base font-semibold text-foreground-dark">Case Details</span>
								<span className="text-base text-foreground/70">Summary of the Case</span>
							</div>
							<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
								<div className="col-span-12 flex">
									<FormField
										control={form.control}
										name="case_category"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Case Category</FormLabel>
													<FormDescription />
												</div>
												<FormGroup>
													<Select onValueChange={field.onChange} defaultValue={field.value} options={caseCategories} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-12 flex">
									<FormField
										control={form.control}
										name="case_type"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Case Type</FormLabel>
													<FormDescription />
												</div>
												<FormGroup>
													<Select onValueChange={field.onChange} defaultValue={field.value} options={caseTypes || []} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-12 flex">
									<FormField
										control={form.control}
										name="case_title"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Case Title/Particulars</FormLabel>
													<FormDescription />
												</div>
												<FormGroup>
													<Textarea {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>
						</div>
					)}

					<div className="flex flex-col w-full gap-3">
						<div className="flex flex-col w-full">
							<span className="text-base font-semibold text-foreground-dark">Personal Information</span>
							<span className="text-base text-foreground/70">Personal Information</span>
						</div>
						<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
							<div className="col-span-12 w-full">
								<FormField
									control={form.control}
									name="party_type"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Party Type</FormLabel>
												<FormDescription />
											</div>
											<FormGroup>
												<Select onValueChange={field.onChange} defaultValue={field.value} options={partyTypes} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-4 w-full">
								<FormField
									control={form.control}
									name="first_name"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>First Name</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input autoComplete="off" placeholder="First Name" {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-4 w-full">
								<FormField
									control={form.control}
									name="middle_name"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel>Middle Name</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input autoComplete="off" placeholder="Middle Name" {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-4 w-full">
								<FormField
									control={form.control}
									name="last_name"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Last Name</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input autoComplete="off" placeholder="Last Name" {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-8 w-full">
								<FormField
									control={form.control}
									name="email"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Email Address</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input autoComplete="off" placeholder="Email Address" {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-4 w-full">
								<FormField
									control={form.control}
									name="mobile_number"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Mobile Number</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input autoComplete="off" placeholder="Mobile Number" {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>
					{attachment !== false && (
						<div className="flex flex-col w-full gap-3">
							<div className="flex flex-col w-full">
								<span className="text-base font-semibold text-foreground-dark">Upload Files</span>
								<span className="text-base text-foreground/70">Some form group requires description, this is the right place for that.</span>
							</div>
							<div className="flex flex-col w-full gap-4">
								{payments === true && <CaseAttachments title="Payment Receipts" hasAttachments={false} />}
								<CaseAttachments title="Case Complaints Documents" hasAttachments={false} />
							</div>
						</div>
					)}
					{payments === true && (
						<>
							<div className="col-span-12 flex flex-col w-full">
								<FormField
									control={form.control}
									name="number_of_summons"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Number of Summons/Notices to be Issued</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input autoComplete="off" placeholder="Number of Summons/Notices to be Issued" {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-12 flex flex-col w-full">
								<DataTable data={fees} columns={columns} />
							</div>

							{upload === true && (
								<div className="col-span-12 w-full">
									<CaseCustomAttachments title="Case Complaints Documents" />
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</Form>
	);
}
